/**
 * MedCom Sendte Beskeder
 */

import { useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Forward, Mail, Send } from 'lucide-react';
import {
  useCareCommunicationsList,
  useExportCareCommunication,
  type CareCommunicationListFilters,
  type CareCommunicationListItem,
} from '@/hooks/useCareCommunications';
import { careCommunicationThreadPath } from '@/lib/medcom/threadDisplay';
import { acknowledgementLabel, inboxStatusLabel } from '@/pages/medcom/MedComInbox';
import { isAutomaticResend } from '@/pages/medcom/acknowledgementDisplay';
import { Banner } from '@/components/ui/Banner';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { ResponsiveTable, type TableColumn } from '@/components/ui/Table';
import { CareCommunicationPriorityChip } from '@/components/medcom/CareCommunicationPriorityChip';
import { OrganizationName } from '@/components/medcom/OrganizationName';
import { topicStandIn } from '@/lib/medcom/letterDisplay';
import { LetterCategory, LetterTopic } from '@/pages/communicate/LetterColumns';
import { TYPE, TYPE_TABULAR } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { formatDistanceToNow } from 'date-fns';
import { da } from 'date-fns/locale';

interface Props {
  clinicId: string | null;
}

function letterHref(item: CareCommunicationListItem): string {
  return careCommunicationThreadPath(item.communicationIdentifier, item.id);
}

function stopRowInteractive(event: MouseEvent) {
  event.stopPropagation();
}

export function MedComSent({ clinicId }: Props) {
  const { t } = useTranslation();
  const exportMutation = useExportCareCommunication();

  const [filters] = useState<CareCommunicationListFilters>({
    direction: 'out', // Only show sent messages
    status: 'all',
  });

  const { data, isLoading, error, refetch } = useCareCommunicationsList(clinicId, filters);
  const messages = data?.items ?? [];

  const handleExport = async (messageId: string, format: 'json' | 'xml') => {
    if (!clinicId) return;
    try {
      await exportMutation.mutateAsync({ clinicId, messageId, format });
    } catch (err) {
      console.error('Eksport fejlede:', err);
    }
  };

  const handleExportEnvelope = async (messageId: string) => {
    if (!clinicId) return;
    try {
      await exportMutation.mutateAsync({
        clinicId,
        messageId,
        format: 'xml',
        envelope: true,
      });
    } catch (err) {
      console.error('Eksport fejlede:', err);
    }
  };

  const columns: TableColumn<CareCommunicationListItem>[] = [
    {
      id: 'status',
      header: t('communicate.col_status'),
      cell: (msg) => <SentStatusCell msg={msg} />,
    },
    {
      id: 'topic',
      header: t('communicate.col_topic'),
      cell: (msg) => <SentTopicCell item={msg} />,
    },
    {
      id: 'category',
      header: t('communicate.col_category'),
      cell: (msg) => <LetterCategory category={msg.category} />,
      hideOnMobile: true,
    },
    {
      id: 'receiver',
      header: t('communicate.col_receiver'),
      cell: (msg) => (
        <OrganizationName name={msg.receiverOrganization} sorId={msg.receiverSor} fallback="—" />
      ),
    },
    {
      id: 'patient',
      header: t('communicate.col_patient'),
      cell: (msg) => msg.patientName?.trim() || '—',
    },
    {
      id: 'date',
      header: t('communicate.col_date'),
      cell: (msg) => (
        <span className={TYPE_TABULAR}>
          {msg.sentAt
            ? formatDistanceToNow(new Date(msg.sentAt), { addSuffix: true, locale: da })
            : msg.createdAt
              ? formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true, locale: da })
              : '—'}
        </span>
      ),
    },
    {
      id: 'error',
      header: t('communicate.col_ack'),
      cell: (msg) =>
        msg.latestAcknowledgement?.errorMessage ? (
          <span className="text-ds-red-ink">Fejl: {msg.latestAcknowledgement.errorMessage}</span>
        ) : (
          <span className="text-ds-muted">—</span>
        ),
      hideOnMobile: true,
    },
    {
      id: 'row-actions',
      header: <span className="sr-only">{t('communicate.forward')}</span>,
      cell: (msg) => (
        <SentRowActions
          msg={msg}
          exporting={exportMutation.isPending}
          onExport={handleExport}
          onExportEnvelope={handleExportEnvelope}
        />
      ),
    },
  ];

  return (
    <div>
      {error ? (
        <div className="mb-s4">
          <Banner
            tone="error"
            action={
              <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
                {t('app.retry')}
              </Button>
            }
          >
            {t('communicate.load_error')}
          </Banner>
        </div>
      ) : null}

      {isLoading ? (
        <Card>
          <div className="flex flex-col gap-s3" aria-busy>
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        </Card>
      ) : !data || messages.length === 0 ? (
        <Card>
          <EmptyState icon={<Send className="h-7 w-7" />} title={t('medcomSection.empty_sent')} />
        </Card>
      ) : (
        <Card className="p-0 nav:p-0">
          <ResponsiveTable
            columns={columns}
            rows={messages}
            getRowKey={(msg) => msg.id}
            getRowHref={(msg) => letterHref(msg)}
            getRowLabel={(msg) => {
              const categoryName = msg.category?.trim()
                ? t(`communicate.category_${msg.category}`, { defaultValue: msg.category })
                : null;
              return (
                topicStandIn(msg.topic, categoryName)?.text ||
                msg.patientName?.trim() ||
                msg.communicationIdentifier
              );
            }}
            empty={null}
            mobileCard={(msg) => (
              <div className="w-full text-left">
                <SentTopicCell item={msg} />
                <div className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
                  <OrganizationName
                    name={msg.receiverOrganization}
                    sorId={msg.receiverSor}
                    fallback="—"
                  />
                  {' · '}
                  {msg.patientName?.trim() || '—'}
                </div>
                <div className="mt-s2">
                  <SentStatusCell msg={msg} />
                </div>
              </div>
            )}
          />
        </Card>
      )}
    </div>
  );
}

function SentTopicCell({ item }: { item: CareCommunicationListItem }) {
  return (
    <Link
      to={letterHref(item)}
      tabIndex={-1}
      onClick={stopRowInteractive}
      className={cn(TYPE.body, 'block font-medium text-ds-text')}
    >
      <LetterTopic topic={item.topic} category={item.category} />
    </Link>
  );
}

function SentStatusCell({ msg }: { msg: CareCommunicationListItem }) {
  const { t } = useTranslation();
  const tone =
    msg.status === 'sent' || msg.status === 'delivered' || msg.status === 'acknowledged'
      ? 'bg-ds-green-tint text-ds-green-ink'
      : msg.status === 'queued'
        ? 'bg-ds-teal-tint text-ds-teal'
        : msg.status === 'failed'
          ? 'bg-ds-red-tint text-ds-red-ink'
          : 'bg-ds-neutral-tint text-ds-text';
  return (
    <span className="inline-flex flex-wrap items-center gap-s2">
      <span className={`rounded px-s2 py-s1 ${tone}`}>{inboxStatusLabel(msg.status)}</span>
      {isAutomaticResend(msg) ? (
        <span className="rounded bg-ds-teal-tint px-s2 py-s1 text-ds-teal">
          {t('medcomSection.resent')}
        </span>
      ) : null}
      {msg.latestAcknowledgement ? (
        <span
          className={`rounded px-s2 py-s1 ${
            msg.latestAcknowledgement.ackType === 'AA'
              ? 'bg-ds-green-tint text-ds-green-ink'
              : msg.latestAcknowledgement.ackType === 'AE'
                ? 'bg-ds-red-tint text-ds-red-ink'
                : 'bg-ds-amber-tint text-ds-amber-ink'
          }`}
        >
          Kvittering: {acknowledgementLabel(msg.latestAcknowledgement.ackType)}
        </span>
      ) : null}
      <CareCommunicationPriorityChip priority={msg.priority} variant="compact" />
    </span>
  );
}

function SentRowActions({
  msg,
  exporting,
  onExport,
  onExportEnvelope,
}: {
  msg: CareCommunicationListItem;
  exporting: boolean;
  onExport: (messageId: string, format: 'json' | 'xml') => void;
  onExportEnvelope: (messageId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-s1">
      <ButtonLink
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        aria-label={t('communicate.forward')}
        to={`/medcom/test?action=forward&threadId=${encodeURIComponent(msg.communicationIdentifier)}&messageId=${encodeURIComponent(msg.id)}`}
        onClick={stopRowInteractive}
      >
        <Forward className="h-4 w-4" />
      </ButtonLink>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={t('communicate.export_json')}
        disabled={exporting}
        onClick={(event) => {
          event.stopPropagation();
          void onExport(msg.id, 'json');
        }}
      >
        JSON
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={t('communicate.export_xml')}
        disabled={exporting}
        onClick={(event) => {
          event.stopPropagation();
          void onExport(msg.id, 'xml');
        }}
      >
        XML
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        aria-label={t('communicate.export_envelope')}
        disabled={exporting}
        onClick={(event) => {
          event.stopPropagation();
          void onExportEnvelope(msg.id);
        }}
      >
        <Mail className="h-4 w-4" />
      </Button>
    </div>
  );
}
